/**
 * Module Toggle Panel
 * Allows live toggling of intelligence modules on/off.
 * Includes pre-configured demo presets for sales reps.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Settings2, Puzzle, Activity, DollarSign, Megaphone, Users, Search, Package, TrendingUp, Shield, ToggleLeft, ToggleRight, ChevronDown, Layers, Sparkles, CheckCircle2 } from 'lucide-react';
import { ALL_MODULES } from '@/config/moduleSectionMap';
import { useClient } from '@/contexts/ClientContext';

const MODULE_ICONS = {
  operations: Activity,
  financial: DollarSign,
  marketing: Megaphone,
  customer: Users,
  staff: Users,
  market_intel: Search,
  inventory: Package,
  projections: TrendingUp,
  compliance: Shield
};

export const ModuleTogglePanel = ({ compact = false }) => {
  const { enabledModules, toggleModule, setEnabledModules, activePreset, applyPreset } = useClient();
  const [showPresets, setShowPresets] = useState(false);
  const [presets, setPresets] = useState([]);
  
  // Load presets from backend when component mounts (guaranteed to be authenticated)
  useEffect(() => {
    const loadPresets = async () => {
      try {
        const res = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/modules/presets`, { credentials: 'include', 
          headers: {}
        });
        if (res.ok) {
          const data = await res.json();
          setPresets(data.presets || []);
        }
      } catch (e) { /* non-blocking */ }
    };
    loadPresets();
  }, []);
  
  const enableAll = () => setEnabledModules(ALL_MODULES.map(m => m.id));
  const disableAll = () => setEnabledModules([]);

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="module-toggle-compact">
        {ALL_MODULES.map(mod => {
          const isOn = enabledModules.includes(mod.id);
          const Icon = MODULE_ICONS[mod.id] || Puzzle;
          return (
            <button
              key={mod.id}
              onClick={() => toggleModule(mod.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                isOn
                  ? 'bg-white border-slate-200 text-slate-700 shadow-sm'
                  : 'bg-slate-100/50 border-dashed border-slate-200 text-slate-400'
              }`}
              data-testid={`module-chip-${mod.id}`}
              title={mod.name}
            >
              <Icon className="w-3 h-3" />
              <span className="hidden sm:inline">{mod.shortName}</span>
              {isOn ? (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <Card className="glass-card" data-testid="module-toggle-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-indigo-500" />
            Active Modules
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {enabledModules.length} / {ALL_MODULES.length} active
            </Badge>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={enableAll} data-testid="enable-all-modules">
              <ToggleRight className="w-3 h-3 mr-1" />All On
            </Button>
            <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={disableAll} data-testid="disable-all-modules">
              <ToggleLeft className="w-3 h-3 mr-1" />All Off
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Toggle modules to see how the dashboard adapts. More modules = more intelligence.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Demo Presets */}
        {presets.length > 0 && (
          <div>
            <button
              onClick={() => setShowPresets(!showPresets)}
              className="flex items-center gap-2 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors w-full"
              data-testid="presets-toggle"
            >
              <Layers className="w-3.5 h-3.5" />
              Quick Presets — Pre-configured for different prospect meetings
              <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${showPresets ? 'rotate-180' : ''}`} />
            </button>
            
            {showPresets && (
 <div className="grid grid-cols-1 gap-2 mt-3" data-testid="presets-grid">
                {presets.map(preset => {
                  const isActive = activePreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => applyPreset(preset.id)}
                      className={`text-left p-3 rounded-xl border transition-all hover:shadow-sm ${
                        isActive
                          ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                          : 'bg-white/60 border-slate-200 hover:border-indigo-200'
                      }`}
                      data-testid={`preset-${preset.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {isActive ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        )}
                        <span className={`text-xs font-semibold ${isActive ? 'text-indigo-700' : 'text-slate-700'}`}>
                          {preset.name}
                        </span>
                        <Badge variant="outline" className="text-[10px] ml-auto">
                          {preset.module_count} modules
                        </Badge>
                      </div>
                      <p className="text-[10px] text-slate-500 leading-relaxed">{preset.description}</p>
                      <p className="text-[10px] text-slate-400 mt-1 italic">{preset.use_case}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Module Grid */}
 <div className="grid grid-cols-1 gap-2">
          {ALL_MODULES.map(mod => {
            const isOn = enabledModules.includes(mod.id);
            const Icon = MODULE_ICONS[mod.id] || Puzzle;
            return (
              <div
                key={mod.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                  isOn
                    ? 'bg-white border-slate-200 shadow-sm'
                    : 'bg-slate-50/50 border-dashed border-slate-200'
                }`}
                onClick={() => toggleModule(mod.id)}
                data-testid={`module-toggle-${mod.id}`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                    isOn ? `bg-gradient-to-br ${mod.gradient} shadow-sm` : 'bg-slate-100'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isOn ? 'text-white' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${isOn ? 'text-slate-800' : 'text-slate-400'}`}>
                    {mod.shortName}
                  </p>
                  <p className="text-[10px] text-slate-400 truncate">{mod.description}</p>
                </div>
                <Switch
                  checked={isOn}
                  onCheckedChange={() => toggleModule(mod.id)}
                  className="shrink-0"
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ModuleTogglePanel;
